import { ChangeEvent, CSSProperties, memo, useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { shallowEqual } from "react-redux"
import { AutoSizer } from "react-virtualized/dist/commonjs/AutoSizer"
import { areEqual, FixedSizeList } from "react-window"

import { useAppDispatch, useAppSelector } from "@/App"
import { Card } from "@/components/Card"
import { Panel } from "@/components/Panel"
import panelCls from "@/components/Panel/Panel.module.scss"
import { Status } from "@/components/Status"
import { DriverContextMenu, DriverContextMenuAction, DriverItemContext } from "@/modules/DriverContextMenu"
import { selectCount } from "@/store"
import { selectDriverById, selectDriversStatus, selectFilteredDriversIds } from "@/store/drivers"
import { setDriversPanelSearchValue, setDriversPanelSearchVisibility, setDriversPanelTab } from "@/store/ui"
import { Driver } from "@/types"
import { DriverUtils } from "@/utils"

import { DriverItem } from "./DriverItem"
import { DriverPanel } from "./DriverPanel"
import cls from "./Drivers.module.scss"

const { filterByTab, filterBySearch, filterByStats, filterByPayments } = DriverUtils

const TABS = ["free", "busy", "offline"] as const
const STATS_TABS = ["inactive", "blocked", "late"] as const
const PAYMENTS_TABS = ["no_cash", "no_card"] as const

export type DriversStatsTab = typeof STATS_TABS[number]
export type DriversPaymentsTab = typeof PAYMENTS_TABS[number]
export type DriversPanelTab = typeof TABS[number] | DriversStatsTab | DriversPaymentsTab | null

export const DriversPanel = ({ className }: { className: string }) => {
  const dispatch = useAppDispatch()

  const currentTab = useAppSelector((state) => state.ui.driversPanelTab)
  const [areStatsTabsExpanded, setAreStatsTabsExpanded] = useState(isStatsTab(currentTab) || isStatsTabSetFromParam())
  const setCurrentTab = useCallback((tabToSet: DriversPanelTab) => dispatch(setDriversPanelTab(tabToSet)), [dispatch])

  const searchValue = useAppSelector((state) => state.ui.driversPanelSearchValue)
  const isSearchBoxShown = useAppSelector((state) => state.ui.isDriversPanelSearchVisible)
  const setSearchValue = useCallback((value: string) => dispatch(setDriversPanelSearchValue(value)), [dispatch])

  const filterDriverBySearch = useCallback((driver: Driver) => filterBySearch(driver, searchValue), [searchValue])
  const filterDriverByCurrentTab = useCallback((driver: Driver) => getFilterByTab(driver, currentTab), [currentTab])

  const filteredDriversIds = useAppSelector(
    selectFilteredDriversIds(filterDriverByCurrentTab, filterDriverBySearch),
    shallowEqual
  )

  const driversStatus = useAppSelector(selectDriversStatus)
  const showDriverDetails = useAppSelector((state) => state.ui.showDriverDetails, shallowEqual)

  const listRef = useRef<FixedSizeList | null>(null)

  useScrollToDriverOnFocus({ currentTab, setCurrentTab, filteredDriversIds, listRef })

  const handleTabChange = useCallback(
    (tabToSet: DriversPanelTab) => {
      setCurrentTab(currentTab !== tabToSet ? tabToSet : null)
      listRef.current?.scrollToItem(0, "start")
    },
    [setCurrentTab, currentTab]
  )

  const [driverContextMenuAction, setDriverContextMenuAction] = useState<DriverContextMenuAction>(null)

  const { t } = useTranslation()

  function toggleSearchBox() {
    dispatch(setDriversPanelSearchVisibility(!isSearchBoxShown))
    setSearchValue("")
  }

  useEffect(() => {
    return () => {
      setSearchValue("")
      dispatch(setDriversPanelSearchVisibility(false))
    }
  }, [setSearchValue, dispatch])

  if (driversStatus !== "fetched") {
    return (
      <Panel wrapperClassName={className}>
        <Status type={driversStatus} title={t("couriers")} fullHeight />
      </Panel>
    )
  }

  if (showDriverDetails) {
    return (
      <Panel wrapperClassName={className}>
        <DriverItemContext.Provider
          value={{
            driverContextMenuAction,
            setDriverContextMenuAction,
          }}
        >
          <DriverPanel driverId={showDriverDetails} />
          
          <DriverContextMenu />
        </DriverItemContext.Provider>
      </Panel>
    )
  }

  return (
    <Panel wrapperClassName={className}>
      <DriverItemContext.Provider
        value={{
          driverContextMenuAction,
          setDriverContextMenuAction,
        }}
      >
        <Panel.Header>
          <Panel.Title>
            {isSearchBoxShown ? (
              <Panel.SearchInput
                value={searchValue}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
                placeholder={t("search_driver")}
              />
            ) : (
              <Panel.Name>{t("couriers")}</Panel.Name>
            )}
          </Panel.Title>

          <Panel.SearchButton
            isActive={isSearchBoxShown}
            title={isSearchBoxShown ? "Zamknij wyszukiwanie" : t("search_driver")}
            onClick={toggleSearchBox}
          />

          <Panel.Tabs>
            {TABS.map((tab) => (
              <DriversTab
                key={tab}
                tab={tab}
                currentTab={currentTab}
                searchValue={searchValue}
                handleTabChange={handleTabChange}
              />
            ))}

            <Panel.TabsDropdown active={areStatsTabsExpanded} setActive={setAreStatsTabsExpanded} />

            {areStatsTabsExpanded && (
              <>
                {STATS_TABS.map((tab) => (
                  <DriversTab
                    key={tab}
                    tab={tab}
                    currentTab={currentTab}
                    searchValue={searchValue}
                    handleTabChange={handleTabChange}
                  />
                ))}

                {PAYMENTS_TABS.map((tab) => (
                  <DriversTab
                    key={tab}
                    tab={tab}
                    currentTab={currentTab}
                    searchValue={searchValue}
                    handleTabChange={handleTabChange}
                  />
                ))}
              </>
            )}
          </Panel.Tabs>
        </Panel.Header>

        <Panel.Content>
          {filteredDriversIds.length > 0 ? (
            <AutoSizer>
              {({ height, width }) => (
                <Card className={panelCls.card}>
                  <FixedSizeList
                    ref={listRef}
                    className={cls.driversList}
                    height={height}
                    width={width}
                    itemData={filteredDriversIds}
                    itemCount={filteredDriversIds.length}
                    itemKey={(index, data) => data[index]}
                    itemSize={130}
                    overscanCount={4}
                  >
                    {DriversRow}
                  </FixedSizeList>
                </Card>
              )}
            </AutoSizer>
          ) : (
            <div className={cls.empty}>{t("no_couriers")}</div>
          )}
        </Panel.Content>

        <DriverContextMenu />
      </DriverItemContext.Provider>
    </Panel>
  )
}

type DriversRowProps = {
  data: Array<Driver["id"]>
  index: number
  style: CSSProperties
}

const DriversRow = memo(({ data, index, style }: DriversRowProps) => {
  const driverId = data[index]
  return <DriverItem key={driverId} driverId={driverId} style={{ ...style, padding: "1.5rem" }} />
}, areEqual)

type DriversTabProps = {
  tab: DriversPanelTab
  currentTab: DriversPanelTab
  searchValue: string
  handleTabChange: (tabToSet: DriversPanelTab) => void
}

const DriversTab = memo(({ tab, currentTab, searchValue, handleTabChange }: DriversTabProps) => {
  const filterDriverByTab = useCallback((driver: Driver) => getFilterByTab(driver, tab), [tab])
  const filterDriverBySearch = useCallback((driver: Driver) => filterBySearch(driver, searchValue), [searchValue])
  const driversForTabCount = useAppSelector(
    selectCount(selectFilteredDriversIds(filterDriverByTab, filterDriverBySearch))
  )

  const { t } = useTranslation()

  return (
    <Panel.Tab
      label={t(`drivers:${tab}`)}
      value={driversForTabCount}
      active={currentTab === tab}
      onClick={() => handleTabChange(tab)}
    />
  )
})

type useScrollToDriverOnFocusOptions = {
  currentTab: DriversPanelTab
  filteredDriversIds: Array<Driver["id"]>
  listRef: React.MutableRefObject<FixedSizeList | null>
  setCurrentTab: (tabToSet: DriversPanelTab) => void
}

function useScrollToDriverOnFocus({
  currentTab,
  filteredDriversIds,
  listRef,
  setCurrentTab,
}: useScrollToDriverOnFocusOptions) {
  const focusedDriverId = useAppSelector((state) => state.ui.focusedDriverId)
  const focusedDriver = useAppSelector(selectDriverById(focusedDriverId!), shallowEqual)
  const focusedDriverIndex = filteredDriversIds.indexOf(focusedDriverId!)

  useEffect(() => {
    if (!focusedDriver) return
    if (currentTab !== focusedDriver.state) setCurrentTab(focusedDriver.state)
    // eslint-disable-next-line
  }, [focusedDriverId])

  useEffect(() => {
    if (!focusedDriver) return

    // setTimeout(() => {
    listRef.current?.scrollToItem(focusedDriverIndex)
    // }, 0) // schedules execution after current call stack has executed

    // eslint-disable-next-line
  }, [focusedDriverId, focusedDriverIndex])
}

function isStatsTab(checkedTab: string | null): checkedTab is DriversStatsTab {
  return STATS_TABS.some((tab) => tab === checkedTab)
}

function isPaymentsTab(checkedTab: string | null): checkedTab is DriversPaymentsTab {
  return PAYMENTS_TABS.some((tab) => tab === checkedTab)
}

function isStatsTabSetFromParam() {
  return (
    window.location.search.includes("drivers_tab") &&
    isStatsTab(new URLSearchParams(window.location.search).get("drivers_tab"))
  )
}

function getFilterByTab(driver: Driver, tab: DriversPanelTab): boolean {
  return isStatsTab(tab)
    ? filterByStats(driver, tab)
    : isPaymentsTab(tab)
    ? filterByPayments(driver, tab)
    : filterByTab(driver, tab)
}
